// src/config/api.ts
import { API_CONFIG, getApiUrl, getDefaultHeaders, ApiResponse, PaginatedResponse, ApiException } from './api.config';
import type { FinderRequest, FinderResponse } from '../components/shared/matterFinder.types';
import { getAuthToken as getSharedAuthToken } from '@/api/axios';

// Enhanced API client with Keycloak integration
class ApiClient {
  private defaultTimeout: number;

  constructor() {
    this.defaultTimeout = API_CONFIG.TIMEOUT;
  }

  // Get token from Keycloak (using robust multi-fallback approach)
  private async getAuthToken(): Promise<string | null> {
    return getSharedAuthToken();
  }

  // Core request method with error handling and retries
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount = 0
  ): Promise<ApiResponse<T>> {
    try {
      const token = await this.getAuthToken();
      const url = getApiUrl(endpoint);
      
      // Get realm information for multi-tenant requests
      const realmId = localStorage.getItem('edgelexFirmId');
      const headers: Record<string, string> = {
        ...getDefaultHeaders(token || undefined),
        ...(options.headers as Record<string, string> || {}),
      };
      if (typeof FormData !== 'undefined' && options.body instanceof FormData) {
        delete headers['Content-Type'];
      }
      
      // Add realm ID to headers for backend multi-tenant support
      if (realmId) {
        headers['X-Realm-ID'] = realmId;
      }
      
      const config: RequestInit = {
        ...options,
        headers,
        signal: options.signal ?? AbortSignal.timeout(this.defaultTimeout),
      };

      const response = await fetch(url, config);
      
      if (!response.ok) {
        let errorDetails: unknown;
        try {
          errorDetails = await response.json();
        } catch {
          try {
            const text = await response.text();
            if (text) errorDetails = { message: text };
          } catch {
            // no-op
          }
        }

        const parsedMessage = (errorDetails as any)?.message || (errorDetails as any)?.error;
        throw new ApiException(
          response.status,
          parsedMessage || `HTTP ${response.status}: ${response.statusText}`,
          'HTTP_ERROR',
          errorDetails
        );
      }

      if (response.status === 204 || response.headers.get('content-length') === '0') {
        return {} as ApiResponse<T>;
      }
      const data = await response.json();
      return data;

    } catch (error) {
      const shouldRetryApiException = error instanceof ApiException && error.status >= 500;
      const method = String(options.method || 'GET').toUpperCase();
      const isSafeRead = method === 'GET' || method === 'HEAD' || method === 'OPTIONS';
      // Automatic retries are limited to safe reads. Mutation callers need an
      // explicit idempotency contract before a transport-level retry is safe.
      if (retryCount < API_CONFIG.RETRY_ATTEMPTS && 
          isSafeRead && (error instanceof TypeError || shouldRetryApiException)) {
        await new Promise(resolve => setTimeout(resolve, API_CONFIG.RETRY_DELAY));
        return this.request<T>(endpoint, options, retryCount + 1);
      }

      // Re-throw the error if retries are exhausted
      throw error;
    }
  }

  // GET request
  async get<T>(endpoint: string, params?: Record<string, unknown> | object): Promise<ApiResponse<T>> {
    const url = new URL(getApiUrl(endpoint));
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    return this.request<T>(url.pathname + url.search);
  }

  /**
   * Reads an endpoint whose JSON body is the domain contract itself rather
   * than EdgeLex's optional { success, data } envelope.
   */
  async getRaw<T>(endpoint: string, params?: Record<string, unknown> | object): Promise<T> {
    return this.get<T>(endpoint, params) as unknown as Promise<T>;
  }

  // POST request
  async post<T>(endpoint: string, data?: unknown, fetchOptions?: Partial<RequestInit>): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      ...fetchOptions,
    });
  }

  async postFormData<T>(endpoint: string, data: FormData): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'POST', body: data });
  }

  // PUT request
  async put<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // DELETE request
  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }

  // PATCH request
  async patch<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}


// Export singleton instance
export const apiClient = new ApiClient();

// --- Entity Enrichment API ---
// Assumed contract:
// POST /api/v1/lex-agent/enrich-message
// Body: { message: string }
// Response: { entities: Entity[] }
import type { Entity } from '@/features/chat/types/entity';

export interface EnrichMessageRequest {
  message: string;
}

export interface EnrichMessageResponse {
  entities: Entity[];
}

/**
 * Enrich a chat message with entity data from the backend.
 * @param message The message text to enrich
 * @returns Promise<EnrichMessageResponse>
 */
export async function enrichMessage(message: string): Promise<EnrichMessageResponse> {
  // Endpoint assumed as /api/v1/lex-agent/enrich-message
  const response = await apiClient.post<EnrichMessageResponse>('/api/v1/lex-agent/enrich-message', { message });
  // If backend wraps in ApiResponse, extract .data
  if ('data' in response && response.data) {
    return response.data;
  }
  // Fallback: if response is already EnrichMessageResponse
  return response as unknown as EnrichMessageResponse;
}

// Contact API Types (based on backend DMS Client model)
export interface Contact {
  id: string;
  realm_id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  contact_type: 'individual' | 'company';
  status: 'lead' | 'qualified' | 'converted' | 'archived';
  tags?: string[];
  assigned_user_id?: string;
  custom_fields?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  last_contact_date?: string;
  notes?: string;
}

export interface ContactCreateData {
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  contact_type: 'individual' | 'company';
  status?: 'lead' | 'qualified' | 'converted' | 'archived';
  tags?: string[];
  assigned_user_id?: string;
  custom_fields?: Record<string, unknown>;
  notes?: string;
}

export interface ContactFilters {
  status?: string;
  contact_type?: string;
  assigned_user_id?: string;
  tags?: string;
  search?: string;
  created_after?: string;
  created_before?: string;
  page?: number;
  limit?: number;
}

/**
 * Client projection returned by the DMS client endpoints. This intentionally
 * mirrors the backend's snake_case contract instead of pretending the endpoint
 * returns the generic Contact/ApiResponse shape.
 */
export interface DmsClientSummary {
  client_id: string;
  client_name: string;
  contact_info?: Record<string, unknown> | null;
  status?: string;
  realm_id: string;
  created_at?: string;
  updated_at?: string;
  created_by_user_id?: string;
  assigned_to_user_id?: string;
  intake_notes?: string;
  client_language_preference?: string;
  tags?: string[];
}

export interface DmsClientsResponse {
  clients: DmsClientSummary[];
  total: number;
  format: 'dms';
  searchOptions?: Record<string, unknown>;
}

// Contact API Methods
export const contactApi = {
  // Get all contacts with optional filtering
  getAll: (filters?: ContactFilters): Promise<PaginatedResponse<Contact>> =>
    apiClient.get<Contact[]>(API_CONFIG.ENDPOINTS.DMS.CLIENTS, filters),

  // Get contact by ID
  getById: (id: string): Promise<ApiResponse<Contact>> =>
    apiClient.get<Contact>(`${API_CONFIG.ENDPOINTS.DMS.CLIENTS}/${id}`),

  // Create new contact
  create: (data: ContactCreateData): Promise<ApiResponse<Contact>> =>
    apiClient.post<Contact>(API_CONFIG.ENDPOINTS.DMS.CLIENTS, data),

  // Update existing contact
  update: (id: string, data: Partial<ContactCreateData>): Promise<ApiResponse<Contact>> =>
    apiClient.put<Contact>(`${API_CONFIG.ENDPOINTS.DMS.CLIENTS}/${id}`, data),

  // Delete contact
  delete: (id: string): Promise<ApiResponse<void>> =>
    apiClient.delete<void>(`${API_CONFIG.ENDPOINTS.DMS.CLIENTS}/${id}`),

  // Search contacts
  search: (query: string): Promise<DmsClientsResponse> =>
    apiClient.getRaw<DmsClientsResponse>(API_CONFIG.ENDPOINTS.DMS.CLIENTS, { search: query }),

  // Get communications linked to a client
  getCommunications: (clientId: string, params?: { page?: number; limit?: number }): Promise<any> =>
    apiClient.get(`${API_CONFIG.ENDPOINTS.DMS.CLIENTS}/${clientId}/communications`, params),
};

// Matter API Types (based on backend DMS Matter model)
export interface Matter {
  id: string;
  realm_id: string;
  client_id: string;
  matter_number: string;
  title: string;
  description?: string;
  practice_area: string;
  case_subtype?: string;
  status: 'active' | 'closed' | 'on_hold' | 'pending';
  stage: string;
  assigned_attorney_id?: string;
  billing_contact_id?: string;
  custom_fields?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  estimated_value?: number;
  actual_value?: number;
  target_resolution_date?: string;
  // Archive identity — the backend list projection always returns these so an
  // intentionally visible archived matter is distinguishable from a working one
  // (matters.service.ts transform). Mirrors MatterDetailed in types/matters.ts.
  is_archived?: boolean;
  is_visible_when_closed?: boolean;
  archived_date?: string;
  // Relationships
  client?: Contact;
}

export interface MatterCreateData {
  client_id: string;
  title: string;
  description?: string;
  practice_area: string;
  status?: 'active' | 'closed' | 'on_hold' | 'pending';
  stage?: string;
  assigned_attorney_id?: string;
  billing_contact_id?: string;
  custom_fields?: Record<string, unknown>;
  estimated_value?: number;
  target_resolution_date?: string;
}

export interface MatterFilters {
  client_id?: string;
  status?: string;
  stage?: string;
  practice_area?: string;
  assigned_attorney_id?: string;
  search?: string;
  created_after?: string;
  created_before?: string;
  page?: number;
  limit?: number;
}

export interface ClientMattersResponse {
  client_id: string;
  matters: Matter[];
  total: number;
}

// Matter API Methods
/**
 * Matter Finder: the universal Client and Matter picker's read (ranked search,
 * context suggestions, recents) and its write (remember a pick).
 * The endpoint returns the body directly, not the {success,data} wrapper.
 */
// The finder lives on the matters router (/api/v1/matters), not the DMS alias.
const MATTER_FINDER_BASE = '/api/v1/matters';

export const matterFinderApi = {
  find: async (request: FinderRequest): Promise<FinderResponse> =>
    (await apiClient.post<FinderResponse>(`${MATTER_FINDER_BASE}/find`, request)) as unknown as FinderResponse,
  recordRecent: async (matterId: string): Promise<void> => {
    await apiClient.post(`${MATTER_FINDER_BASE}/find/recent`, { matterId });
  },
  pin: async (matterId: string): Promise<void> => {
    await apiClient.post(`${MATTER_FINDER_BASE}/find/pins`, { matterId });
  },
  unpin: async (matterId: string): Promise<void> => {
    await apiClient.delete(`${MATTER_FINDER_BASE}/find/pins/${encodeURIComponent(matterId)}`);
  },
};

export const matterApi = {
  // Get all matters with optional filtering
  getAll: (filters?: MatterFilters): Promise<PaginatedResponse<Matter>> =>
    apiClient.get<Matter[]>(API_CONFIG.ENDPOINTS.DMS.MATTERS, filters),

  // Get matter by ID
  getById: (id: string): Promise<ApiResponse<Matter>> =>
    apiClient.get<Matter>(`${API_CONFIG.ENDPOINTS.DMS.MATTERS}/${id}`),

  // Create new matter
  create: (data: MatterCreateData): Promise<ApiResponse<Matter>> =>
    apiClient.post<Matter>(API_CONFIG.ENDPOINTS.DMS.MATTERS, data),

  // Update existing matter
  update: (id: string, data: Partial<MatterCreateData>): Promise<ApiResponse<Matter>> =>
    apiClient.put<Matter>(`${API_CONFIG.ENDPOINTS.DMS.MATTERS}/${id}`, data),

  // Delete matter
  delete: (id: string): Promise<ApiResponse<void>> =>
    apiClient.delete<void>(`${API_CONFIG.ENDPOINTS.DMS.MATTERS}/${id}`),

  // Get matters for specific client
  getByClient: (clientId: string): Promise<ClientMattersResponse> =>
    apiClient.getRaw<ClientMattersResponse>(`${API_CONFIG.ENDPOINTS.DMS.CLIENTS}/${clientId}/matters`),

  // Get production Matter communications from the first-class matter surface.
  getCommunications: (
    matterId: string,
    params?: { page?: number; limit?: number; channel?: 'all' | 'email' | 'sms' | 'meeting' | 'message'; filter?: 'all' | 'unread' | 'privileged' | 'attachments' | 'needs_repair' },
  ): Promise<any> =>
    apiClient.get(`/api/v1/matters/${matterId}/communications`, params),

  repairCommunicationsLinkage: (matterId: string): Promise<any> =>
    apiClient.post(`/api/v1/matters/${matterId}/communications/repair-linkage`),
};

// Intake API Types (based on enhanced-intake.routes.ts)
export interface IntakeSubmission {
  submissionId: string;
  realm_id: string;
  status: 'submitted' | 'processing' | 'completed' | 'failed';
  formData: Record<string, unknown>;
  language: string;
  source: 'web' | 'api' | 'mobile' | 'email' | 'referral';
  created_at: string;
  updated_at: string;
  processing_results?: {
    aiAnalysis?: unknown;
    conflictCheck?: unknown;
    leadScoring?: unknown;
    routing?: unknown;
  };
}

export interface IntakeSubmissionData {
  formData: Record<string, unknown>;
  source?: 'web' | 'api' | 'mobile' | 'email' | 'referral';
  language?: string;
}

// Intake API Methods
export const intakeApi = {
  // Submit new intake form
  submit: (data: IntakeSubmissionData): Promise<ApiResponse<IntakeSubmission>> =>
    apiClient.post<IntakeSubmission>(API_CONFIG.ENDPOINTS.INTAKE.ENHANCED, data),

  // Get intake submission by ID
  getById: (submissionId: string): Promise<ApiResponse<IntakeSubmission>> =>
    apiClient.get<IntakeSubmission>(`${API_CONFIG.ENDPOINTS.INTAKE.ENHANCED}/${submissionId}`),

  // Get all intake submissions
  getAll: (filters?: Record<string, unknown>): Promise<PaginatedResponse<IntakeSubmission>> =>
    apiClient.get<IntakeSubmission[]>(API_CONFIG.ENDPOINTS.INTAKE.ENHANCED, filters),
};

// Export all APIs
export default apiClient;
